import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import BaseModel from "../../../assets/ImageGeneration/images/api-base-model.jpg";
import FastModel from "../../../assets/ImageGeneration/images/api-fast-model.jpg";
import HDModel from "../../../assets/ImageGeneration/images/api-hd-model.jpg";
import PromptEnhance from "../../../assets/ImageGeneration/images/api-prompt-enhancement.jpg";
import Reimagine from "../../../assets/ImageGeneration/images/api-reimagine.jpg";
import VectorBase from "../../../assets/ImageGeneration/images/api-vector-base.jpg";
import VectorFast from "../../../assets/ImageGeneration/images/api-vector-fast.jpg";
import { ConsoleAPICardProps } from "../../../components/Home/API/Cards/Card/ConsoleAPICard";
import ConsoleAPICards from "../../../components/Home/API/Cards/ConsoleAPICards";
import ConsoleAPILayout from "../../../components/Home/API/ConsoleAPILayout";
import BriaButton from "../../../components/common/BriaButton/BriaButton";
import LinkConstants from "../../../constants/LinkConstants";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents";
import { usePostHog } from "../../../hooks/usePostHog";

const ImageGenerationAPI = () => {
	const { t } = useTranslation("translation", { keyPrefix: "imageGeneration.api" });
	const { trackEvent } = usePostHog();

	const openDocs = (link: string) => {
		// Track API documentation viewed
		trackEvent(POSTHOG_EVENTS.API_DOCUMENTATION_VIEWED, {
			section: getSectionFromLink(link),
		});

		window.open(link, "_blank");
	};

	// Helper to extract section name from link
	const getSectionFromLink = (link: string): string => {
		const sectionMap: { [key: string]: string } = {
			[LinkConstants.IMAGE_GENERATION.BASE.DOCS]: "Base Model",
			[LinkConstants.IMAGE_GENERATION.HD.DOCS]: "HD Model",
			[LinkConstants.IMAGE_GENERATION.VECTOR_FAST.DOCS]: "Vector Fast",
			[LinkConstants.IMAGE_GENERATION.PROMPT_ENHANCEMENT.DOCS]: "Prompt Enhancement",
			[LinkConstants.IMAGE_GENERATION.FAST.DOCS]: "Fast Model",
			[LinkConstants.IMAGE_GENERATION.VECTOR_BASE.DOCS]: "Vector Base",
			[LinkConstants.IMAGE_GENERATION.REIMAGINE.DOCS]: "Reimagine",
		};

		return sectionMap[link] || "Unknown Section";
	};

	const imageGenerationApiCards: ConsoleAPICardProps[] = [
		{
			image: BaseModel,
			header: t("cards.baseModel.header"),
			description: t("cards.baseModel.description"),
			buttons: (
				<BriaButton buttonType="textSmall" onClick={() => openDocs(LinkConstants.IMAGE_GENERATION.BASE.DOCS)}>
					{t("cards.baseModel.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_GENERATION.BASE.DOCS),
		},
		{
			image: HDModel,
			header: t("cards.hdModel.header"),
			description: t("cards.hdModel.description"),
			buttons: (
				<BriaButton buttonType="textSmall" onClick={() => openDocs(LinkConstants.IMAGE_GENERATION.HD.DOCS)}>
					{t("cards.hdModel.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_GENERATION.HD.DOCS),
		},
		{
			image: VectorFast,
			header: t("cards.vectorFast.header"),
			description: t("cards.vectorFast.description"),
			buttons: (
				<BriaButton
					buttonType="textSmall"
					onClick={() => openDocs(LinkConstants.IMAGE_GENERATION.VECTOR_FAST.DOCS)}
				>
					{t("cards.vectorFast.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_GENERATION.VECTOR_FAST.DOCS),
		},
		{
			image: PromptEnhance,
			header: t("cards.promptEnhance.header"),
			description: t("cards.promptEnhance.description"),
			buttons: (
				<BriaButton
					buttonType="textSmall"
					onClick={() => openDocs(LinkConstants.IMAGE_GENERATION.PROMPT_ENHANCEMENT.DOCS)}
				>
					{t("cards.promptEnhance.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_GENERATION.PROMPT_ENHANCEMENT.DOCS),
		},
		{
			image: FastModel,
			header: t("cards.fastModel.header"),
			description: t("cards.fastModel.description"),
			buttons: (
				<BriaButton buttonType="textSmall" onClick={() => openDocs(LinkConstants.IMAGE_GENERATION.FAST.DOCS)}>
					{t("cards.fastModel.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_GENERATION.FAST.DOCS),
		},
		{
			image: VectorBase,
			header: t("cards.vectorBase.header"),
			description: t("cards.vectorBase.description"),
			buttons: (
				<BriaButton
					buttonType="textSmall"
					onClick={() => openDocs(LinkConstants.IMAGE_GENERATION.VECTOR_BASE.DOCS)}
				>
					{t("cards.vectorBase.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_GENERATION.VECTOR_BASE.DOCS),
		},
		{
			image: Reimagine,
			header: t("cards.reimagine.header"),
			description: t("cards.reimagine.description"),
			buttons: (
				<BriaButton
					buttonType="textSmall"
					onClick={() => openDocs(LinkConstants.IMAGE_GENERATION.REIMAGINE.DOCS)}
				>
					{t("cards.reimagine.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_GENERATION.REIMAGINE.DOCS),
		},
	];

	return (
		<ConsoleAPILayout description={t("description")}>
			<ConsoleAPICards cards={imageGenerationApiCards} />
		</ConsoleAPILayout>
	);
};

export default observer(ImageGenerationAPI);
